import { useEffect, useState } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { PageHeader, Spinner } from "@/components/shared";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Mail, Trash2, Download, Users, MailCheck, FileDown, Inbox, Check, Minus, Share2 } from "lucide-react";
import { toast } from "sonner";

const fmt = (iso) => { try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };
const FILTERS = [{ v: "", l: "All" }, { v: "optin", l: "Opted in" }];

function Stat({ icon: Icon, label, value, tint }) {
  return (
    <div className="kid-card p-5 flex items-center gap-4" data-testid={`leads-stat-${label.replace(/\s+/g,"-").toLowerCase()}`}>
      <div className="w-11 h-11 rounded-xl grid place-items-center shrink-0" style={{ background: tint }}><Icon size={20} className="text-white" /></div>
      <div>
        <div className="text-2xl font-extrabold text-kid-navy font-display">{value}</div>
        <div className="text-sm text-kid-muted">{label}</div>
      </div>
    </div>
  );
}

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, opted_in: 0, downloaded: 0 });
  const [referrals, setReferrals] = useState({ sources: [], totals: { visits: 0, signups: 0, conversion_rate: 0 } });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [deleting, setDeleting] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = async (f = filter) => {
    const { data } = await api.get("/admin/leads", { params: f === "optin" ? { opted_in: true } : {} });
    setLeads(data.leads); setStats(data.stats); setLoading(false);
  };
  useEffect(() => {
    load();
    api.get("/admin/referrals").then(({ data }) => setReferrals(data)).catch(() => {});
  }, []);

  const changeFilter = (v) => { setFilter(v); setLoading(true); load(v); };

  const doDelete = async () => {
    try { await api.delete(`/admin/leads/${deleting.id}`); toast.success("Lead deleted."); setDeleting(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = filter === "optin" ? { opted_in: true } : {};
      const res = await api.get("/admin/leads/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "kidpath-guide-leads.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed."); }
    finally { setExporting(false); }
  };

  if (loading) return <Spinner />;

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <PageHeader eyebrow="Growth" title="Guide Leads" subtitle="People who downloaded the free funding guide, and who opted into the mailing list." />
        <Button onClick={exportCsv} disabled={exporting || leads.length === 0} className="shrink-0" data-testid="leads-export">
          <FileDown size={16} className="mr-1.5" /> {exporting ? "Exporting…" : "Export CSV"}
        </Button>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <Stat icon={Users} label="Total leads" value={stats.total} tint="var(--teal)" />
        <Stat icon={MailCheck} label="Opted in to email" value={stats.opted_in} tint="var(--green)" />
        <Stat icon={Download} label="Downloaded" value={stats.downloaded} tint="var(--grape)" />
      </div>

      <div className="mb-8" data-testid="referral-section">
        <div className="flex items-center gap-2 mb-3">
          <Share2 size={16} className="text-kid-teal" />
          <h2 className="font-display font-bold text-kid-navy">Referral sources</h2>
          <span className="text-xs text-kid-muted">Visits &amp; signups from shared links (e.g. journal moments)</span>
        </div>
        {referrals.sources.length === 0 ? (
          <div className="kid-card p-6 text-center text-sm text-kid-muted" data-testid="referral-empty">
            No referral traffic yet. When families share a journal moment, visits will show up here.
          </div>
        ) : (
          <div className="kid-card overflow-hidden" data-testid="referral-table">
            <table className="w-full text-sm">
              <thead className="bg-kid-mist text-kid-muted text-left"><tr>
                <th className="px-5 py-3 font-poppins font-semibold">Source</th>
                <th className="px-5 py-3">Visits</th>
                <th className="px-5 py-3">Signups</th>
                <th className="px-5 py-3">Conversion</th>
              </tr></thead>
              <tbody>
                {referrals.sources.map((s) => (
                  <tr key={s.ref} className="border-t border-kid-line" data-testid="referral-row">
                    <td className="px-5 py-3 font-medium text-kid-navy">{s.ref}</td>
                    <td className="px-5 py-3 text-kid-muted">{s.visits}</td>
                    <td className="px-5 py-3 text-kid-muted">{s.signups}</td>
                    <td className="px-5 py-3 font-semibold text-kid-teal">{s.conversion_rate}%</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-kid-line bg-kid-mist/50" data-testid="referral-total-row">
                  <td className="px-5 py-3 font-bold text-kid-navy">Total</td>
                  <td className="px-5 py-3 font-bold text-kid-navy">{referrals.totals.visits}</td>
                  <td className="px-5 py-3 font-bold text-kid-navy">{referrals.totals.signups}</td>
                  <td className="px-5 py-3 font-bold text-kid-teal">{referrals.totals.conversion_rate}%</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="flex gap-2 mb-4">
        {FILTERS.map((f) => (
          <button key={f.v} onClick={() => changeFilter(f.v)}
            className={`text-sm font-semibold px-3.5 py-1.5 rounded-full transition-colors ${filter === f.v ? "bg-kid-teal text-white" : "bg-white text-kid-muted border border-kid-line hover:bg-kid-mist"}`}
            data-testid={`leads-filter-${f.v || "all"}`}>{f.l}</button>
        ))}
      </div>

      {leads.length === 0 ? (
        <div className="kid-card p-10 text-center text-kid-muted" data-testid="leads-empty">
          <Inbox size={28} className="mx-auto mb-3 opacity-60" />
          No leads{filter === "optin" ? " who opted in" : ""} yet.
        </div>
      ) : (
        <div className="kid-card overflow-hidden" data-testid="leads-table">
          <table className="w-full text-sm">
            <thead className="bg-kid-mist text-kid-muted text-left"><tr>
              <th className="px-5 py-3 font-poppins font-semibold">Name</th>
              <th className="px-5 py-3">Email</th>
              <th className="px-5 py-3">Mailing list</th>
              <th className="px-5 py-3">Downloaded</th>
              <th className="px-5 py-3">Requested</th>
              <th className="px-5 py-3"></th>
            </tr></thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-t border-kid-line" data-testid="lead-row">
                  <td className="px-5 py-3 font-medium text-kid-navy">{l.name}</td>
                  <td className="px-5 py-3"><a href={`mailto:${l.email}`} className="text-kid-teal hover:underline inline-flex items-center gap-1"><Mail size={13} /> {l.email}</a></td>
                  <td className="px-5 py-3">
                    {l.opt_in
                      ? <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-kid-green/15 text-[#3f9d5c]"><Check size={12} /> Opted in</span>
                      : <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-kid-mist text-kid-muted"><Minus size={12} /> No</span>}
                  </td>
                  <td className="px-5 py-3 text-kid-muted">{l.downloaded_at ? "Yes" : "—"}</td>
                  <td className="px-5 py-3 text-kid-muted">{fmt(l.created_at)}</td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => setDeleting(l)} className="text-kid-coral p-1.5 rounded-lg hover:bg-kid-mist" title="Delete" data-testid="lead-delete"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="lead-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this lead?</AlertDialogTitle>
            <AlertDialogDescription>{deleting?.name} ({deleting?.email}) will be permanently removed from your leads list.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="lead-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-kid-coral hover:bg-kid-coral/90" data-testid="lead-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
